import type { PrimitiveValue } from "@skilitics/nautilus-core";
import {
  isPrimitiveValue,
  isReferencedValue,
  referencedNodeValue,
  type NodeValueReference,
  type ReferencedValue,
} from "@skilitics/nautilus-core-designer-data";

import type { DesignerNode } from "../types/index.ts";
import type { GetNodeValue } from "./querying/getNodeValue.ts";

interface CreateOptions {
  getNodeValue: GetNodeValue;
}

/**
 * Resolve a stored value to its design-time literal. Returns undefined when
 * the value is only runtime-resolvable (external, or a node value without a
 * design-time spec), when a reference dangles or type-mismatches, or when a
 * reference chain cycles — builders then show their invalid state and publish
 * abstains.
 */
export const resolveDesignTimeValueCreate = ({ getNodeValue }: CreateOptions) => {
  const resolveConfig = (config: ReferencedValue, visited: Set<string>): PrimitiveValue | undefined => {
    const nodeValue = referencedNodeValue(config);
    // an external value is unknown until runtime
    return nodeValue && resolveNodeValue(nodeValue, visited);
  };

  const resolveNodeValue = (config: NodeValueReference, visited: Set<string>): PrimitiveValue | undefined => {
    const visitKey = `${config.nodeId}:${config.name}`;
    if (visited.has(visitKey)) return undefined; // reference cycle
    visited.add(visitKey);

    const resolved = getNodeValue(config);
    if (!resolved) return undefined;

    const { node, descriptor } = resolved;
    const { designTime } = descriptor;
    if (!designTime) return undefined;

    const value =
      "resolve" in designTime ? designTime.resolve(node) : resolveStoredValue(node, designTime.key, visited);
    return typeof value === descriptor.type ? value : undefined;
  };

  const resolveStoredValue = (node: DesignerNode, key: string, visited: Set<string>): PrimitiveValue | undefined => {
    const stored: unknown = node.config.values?.[key];
    if (isReferencedValue(stored)) return resolveConfig(stored, visited);
    return isPrimitiveValue(stored) ? stored : undefined;
  };

  return (config: ReferencedValue): PrimitiveValue | undefined => resolveConfig(config, new Set());
};

export type ResolveDesignTimeValue = ReturnType<typeof resolveDesignTimeValueCreate>;
